import React, { useEffect, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { setInternalOperatorSession } from '../services/internalSession';

const OasixLogo = ({ className = 'w-8 h-8' }: { className?: string }) => (
  <svg viewBox="0 0 100 100" fill="none" stroke="currentColor" strokeWidth="10" strokeLinecap="round" className={className}>
    <ellipse cx="50" cy="80" rx="35" ry="8" />
    <path d="M20 55 C 20 25, 80 25, 80 55" />
    <path d="M35 55 C 35 40, 65 40, 65 55" />
  </svg>
);

export const InternalLoginView: React.FC = () => {
  const { signIn, signOut, isAuthenticated, authRole, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const deniedRef = useRef(false);

  useEffect(() => {
    if (!isAuthenticated || authLoading) return;
    if (authRole === 'super_admin') {
      setInternalOperatorSession(true);
      navigate('/internal/tenants', { replace: true });
      return;
    }
    if (!deniedRef.current) {
      deniedRef.current = true;
      setInternalOperatorSession(false);
      setError('Internal access is restricted to super admins.');
      signOut().catch(() => {});
    }
  }, [isAuthenticated, authLoading, authRole, navigate, signOut]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    deniedRef.current = false;
    setInternalOperatorSession(false);
    try {
      await signIn(email, password);
    } catch (err: any) {
      setError(err?.message || 'Invalid credentials');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-slate-950 px-4">
      <div className="w-full max-w-md">
        <div className="flex flex-col items-center mb-8">
          <div className="bg-indigo-500 p-3 rounded-xl text-white mb-4">
            <OasixLogo className="w-10 h-10" />
          </div>
          <h1 className="text-2xl font-bold text-white">Internal Control Plane</h1>
          <p className="text-slate-300 text-sm mt-1">Super admin sign-in only</p>
        </div>

        <form onSubmit={handleSubmit} className="bg-white rounded-xl shadow-sm border border-slate-200 p-8 space-y-5">
          {error && (
            <div className="bg-red-50 text-red-700 text-sm p-3 rounded-lg border border-red-200">
              {error}
            </div>
          )}

          <div>
            <label htmlFor="internal-login-email" className="block text-sm font-medium text-slate-700 mb-1">Email</label>
            <input
              id="internal-login-email"
              type="email"
              required
              autoComplete="email"
              value={email}
              onChange={e => setEmail(e.target.value)}
              className="w-full p-2.5 border border-slate-300 rounded-lg focus:ring-2 focus:ring-indigo-500 outline-none text-sm"
              placeholder="internal@oasix.app"
            />
          </div>

          <div>
            <label htmlFor="internal-login-password" className="block text-sm font-medium text-slate-700 mb-1">Password</label>
            <input
              id="internal-login-password"
              type="password"
              required
              autoComplete="current-password"
              value={password}
              onChange={e => setPassword(e.target.value)}
              className="w-full p-2.5 border border-slate-300 rounded-lg focus:ring-2 focus:ring-indigo-500 outline-none text-sm"
              placeholder="Enter your password"
            />
          </div>

          <button
            type="submit"
            disabled={loading}
            className="w-full bg-indigo-600 text-white py-2.5 rounded-lg font-medium hover:bg-indigo-700 transition-colors disabled:opacity-50"
          >
            {loading ? 'Signing in...' : 'Sign in to internal'}
          </button>

          <div className="flex justify-between text-sm">
            <Link to="/login" className="text-indigo-600 hover:underline">
              Standard login
            </Link>
            <Link to="/forgot-password" className="text-indigo-600 hover:underline">
              Forgot password?
            </Link>
          </div>
        </form>
      </div>
    </div>
  );
};
